/**
 * ShareGuard — wraps share-prefixed routes (`/share/:token/docs/...`).
 *
 * On every render, it checks whether `sessionStorage` already holds a
 * share-session JWT minted for THIS URL's token. If so, it renders
 * children immediately. If not (a fresh tab opened on a deep-linked
 * share URL, or someone pasted the link into a new browser), it
 * exchanges the share token for a JWT via `/api/share/<token>/session`,
 * stores it, then renders children. On error it redirects to the
 * share-entry route so the user sees the standard "Share unavailable"
 * surface.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";

const SHARE_TOKEN_KEY = "cortex_share_access_token";
const SHARE_META_KEY = "cortex_share_meta";

interface SessionResp {
  accessToken: string;
  expiresAt: string;
  projectSlug: string;
  branchId: string;
  rules: Array<{ slug: string; includeChildren?: boolean; include_children?: boolean }>;
  shareExpiresAt: string | null;
}

function readMetaToken(): string | null {
  try {
    const raw = sessionStorage.getItem(SHARE_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string };
    return parsed.token ?? null;
  } catch {
    return null;
  }
}

export default function ShareGuard({ children }: { children: ReactNode }) {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<"loading" | "ready" | "error">(() => {
    if (!token) return "error";
    const jwt = sessionStorage.getItem(SHARE_TOKEN_KEY);
    return jwt && readMetaToken() === token ? "ready" : "loading";
  });

  useEffect(() => {
    if (!token) { setState("error"); return; }
    const jwt = sessionStorage.getItem(SHARE_TOKEN_KEY);
    if (jwt && readMetaToken() === token) { setState("ready"); return; }

    let cancelled = false;
    setState("loading");
    (async () => {
      try {
        const r = await fetch(`/api/share/${encodeURIComponent(token)}/session`, { method: "POST" });
        if (cancelled) return;
        if (!r.ok) { setState("error"); return; }
        const data = (await r.json()) as SessionResp;
        sessionStorage.setItem(SHARE_TOKEN_KEY, data.accessToken);
        sessionStorage.setItem(SHARE_META_KEY, JSON.stringify({
          token,
          projectSlug: data.projectSlug,
          branchId: data.branchId,
          rules: data.rules,
          shareExpiresAt: data.shareExpiresAt,
        }));
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <div className="size-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          Opening shared document…
        </div>
      </div>
    );
  }

  if (state === "error") {
    return <Navigate to={`/share/${encodeURIComponent(token ?? "")}`} replace />;
  }

  return <>{children}</>;
}
